import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Package,
  CheckCircle,
  XCircle,
  Tag,
  Hash,
  Eye,
  Edit,
  Trash2,
  Plus
} from 'lucide-react'
import { getImageUrl } from '../../config/imageConfig'
import useProductStore from '@/stores/productStore'
import { Product } from '@/services/productService'
import { categoryService, Category } from '@/services/categoryService'
import useAppConfigStore from '@/stores/appConfigStore'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'

const ProductList = () => {
  const {
    products,
    loading,
    error,
    pagination,
    fetchProducts,
    deleteProduct,
    setFilters
  } = useProductStore()

  const { availableCurrencies, initializeAppConfig, loading: configLoading, systemConfig } = useAppConfigStore()
  const navigate = useNavigate()

  const [categories, setCategories] = useState<Category[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)

  // Initialize app config if not already loaded
  useEffect(() => {
    if (availableCurrencies.length === 0 && !configLoading) {
      initializeAppConfig()
    }
  }, [availableCurrencies.length, configLoading, initializeAppConfig])

  // Load products and categories on mount
  useEffect(() => {
    fetchProducts()
    loadCategories()
  }, [])

  const loadCategories = async () => {
    setLoadingCategories(true)
    try {
      const response = await categoryService.getAll({})
      setCategories(response.data || [])
    } catch (err) {
      console.error('Failed to load categories:', err)
    } finally {
      setLoadingCategories(false)
    }
  }

  // Create a mapping of currency code to symbol (case-insensitive)
  const currencySymbolMap = useMemo(() => {
    const map: Record<string, string> = {}
    availableCurrencies.forEach((currency) => {
      const code = currency.code.toUpperCase()
      map[code] = currency.symbol || currency.code
      // Also map lowercase version for consistency
      map[currency.code.toLowerCase()] = currency.symbol || currency.code
    })
    return map
  }, [availableCurrencies])

  // Helper function to format price with currency
  const formatPrice = useCallback((price: number | string | null | undefined, currencyCode?: string) => {
    if (price == null) return 'N/D'
    
    let effectiveCurrencyCode = currencyCode
    
    // If no currency code provided, try to use system default
    if (!effectiveCurrencyCode || effectiveCurrencyCode.trim() === '') {
      effectiveCurrencyCode = systemConfig?.default_currency || 'MAD' // Fallback to MAD
    }
    
    // Try to get symbol from map (case-insensitive)
    const upperCode = effectiveCurrencyCode.toUpperCase()
    const lowerCode = effectiveCurrencyCode.toLowerCase()
    const symbol = currencySymbolMap[upperCode] || currencySymbolMap[lowerCode] || effectiveCurrencyCode
    
    // Convert price to number if needed
    const numericPrice = typeof price === 'number' ? price : parseFloat(price)
    
    // Check if numericPrice is a valid number
    if (isNaN(numericPrice)) {
      return `${symbol} N/D`
    }
    
    return `${symbol} ${numericPrice.toFixed(2)}`
  }, [currencySymbolMap, systemConfig])

  // Create a mapping of category_id to category name
  const categoryMap = useMemo(() => {
    return categories.reduce((acc, category) => {
      acc[category.category_id] = category.name
      return acc
    }, {} as Record<string, string>)
  }, [categories])

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await deleteProduct(id)
      } catch (error) {
        console.error('Error deleting product:', error)
      }
    }
  }

  const handleSearch = (value: string) => {
    console.log('[DEBUG] ProductList handleSearch called with value:', value)
    setFilters({ search: value })
    fetchProducts({ search: value })
  }

  const handleFilterChange = (filters: any[]) => {
    const filterObj: any = {}
    filters.forEach(filter => {
      if (filter.key === 'category_id' && filter.value) {
        filterObj.category_id = filter.value
      } else if (filter.key === 'dietary_tags' && filter.value) {
        filterObj.dietary_tags = filter.value
      } else if (filter.key === 'is_available' && filter.value !== 'all') {
        filterObj.is_available = filter.value === 'available'
      }
    })
    
    setFilters(filterObj)
    fetchProducts(filterObj)
  }

  const handlePageChange = (page: number) => {
    setFilters({ page })
    fetchProducts({ page })
  }

  const handlePageSizeChange = (newSize: number) => {
    setFilters({ limit: newSize })
    fetchProducts({ limit: newSize })
  }

  // Define columns for DataTable
  const columns = [
    {
      key: 'product',
      label: 'Produit',
      sortable: true,
      render: (_value: any, row: Product) => (
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0 bg-primary-100 rounded-lg flex items-center justify-center overflow-hidden">
            {row.images && row.images.length > 0 && row.images[0].image_url ? (
              <img
                src={getImageUrl(row.images[0].image_url)}
                alt={row.name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.innerHTML = '<Package class="h-6 w-6 text-primary-600" />';
                }}
              />
            ) : (
              <Package className="h-6 w-6 text-primary-600" />
            )}
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {row.name}
            </div>
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {row.description ? `${row.description.substring(0, 50)}...` : 'Aucune description'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Catégorie',
      sortable: true,
      render: (_value: any, row: Product) => (
        <div>
          <div className="flex items-center text-sm text-gray-900">
            <Tag className="h-4 w-4 mr-2 text-gray-400" />
            {categoryMap[row.category_id] || row.category_id || 'Non catégorisé'}
          </div>
          <div className="flex items-center text-sm text-gray-500 mt-1">
            <Hash className="h-4 w-4 mr-2 text-gray-400" />
            {row.tags?.slice(0, 2).join(', ') || 'Aucun tag'}
          </div>
        </div>
      )
    },
    {
      key: 'price',
      label: 'Prix & Stock',
      sortable: true,
      render: (_value: any, row: Product) => (
        <div>
          <div className="flex items-center text-sm text-gray-900">
            {formatPrice(row.price, row.currency)}
          </div>
          <div className="flex items-center text-sm text-gray-500 mt-1">
            <Hash className="h-4 w-4 mr-2 text-gray-400" />
            Stock: {row.stock_quantity}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (_value: any, row: Product) => (
        row.is_available ? (
          <Badge variant="default" className="inline-flex items-center bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-4 w-4 mr-1" />
            Disponible
          </Badge>
        ) : (
          <Badge variant="destructive" className="inline-flex items-center">
            <XCircle className="h-4 w-4 mr-1" />
            Indisponible
          </Badge>
        )
      )
    },
    {
      key: 'created_at',
      label: 'Créé le',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-gray-500">
          {new Date(value).toLocaleDateString('fr-FR')}
        </div>
      )
    }
  ]

  // Define actions for DataTable
  const actions = [
    {
      label: 'Voir',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row: Product) => navigate(`/products/${row.product_id}`),
      variant: 'outline' as const
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: (row: Product) => navigate(`/products/${row.product_id}/edit`),
      variant: 'outline' as const
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row: Product) => handleDelete(row.product_id),
      variant: 'destructive' as const
    }
  ]

  const DIETARY_TAG_OPTIONS = [
    { value: '', label: 'Tous les régimes' },
    { value: 'vegetarian', label: 'Végétarien' },
    { value: 'vegan', label: 'Vegan' },
    { value: 'gluten-free', label: 'Sans gluten' },
    { value: 'lactose-free', label: 'Sans lactose' },
    { value: 'organic', label: 'Bio' },
    { value: 'halal', label: 'Halal' },
    { value: 'kosher', label: 'Casher' },
    { value: 'nut-free', label: 'Sans noix' },
  ]

  // Define filter options
  const filterOptions = [
    {
      key: 'search',
      label: 'Recherche',
      type: 'text' as const,
      placeholder: 'Nom, description...'
    },
    {
      key: 'category_id',
      label: 'Catégorie',
      type: 'select' as const,
      options: [
        { value: '', label: 'Toutes les catégories' },
        ...categories.map(category => ({
          value: category.category_id,
          label: category.name
        }))
      ],
      disabled: loadingCategories
    },
    {
      key: 'dietary_tags',
      label: 'Régime alimentaire',
      type: 'select' as const,
      options: DIETARY_TAG_OPTIONS
    },
    {
      key: 'is_available',
      label: 'Disponibilité',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'available', label: 'Disponible' },
        { value: 'unavailable', label: 'Indisponible' }
      ]
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Produits"
        subtitle="Gérez tous les produits de la plateforme"
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: 'Produits', href: '/products' }
        ]}
        actions={[
          {
            label: 'Nouveau produit',
            icon: <Plus className="h-5 w-5" />,
            onClick: () => navigate('/products/new'),
            variant: 'default' as const
          }
        ]}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FilterBar
        searchPlaceholder="Rechercher des produits..."
        onSearch={handleSearch}
        filters={filterOptions}
        onFilterChange={handleFilterChange}
      />

      <DataTable
       data={products}
       columns={columns}
       actions={actions}
       loading={loading}
       pagination={{
         current: pagination.page,
         total: pagination.total,
         pageSize: pagination.limit,
         onPageChange: handlePageChange,
         onPageSizeChange: handlePageSizeChange
       }}
       emptyMessage="Aucun produit trouvé"
     />
    </div>
  )
}

export default ProductList
